<template>
	<nk-page commercialAccess="Salary" policyClaims="Salary_SalaryItem_Management" tabindex="101" startup>
		<template #badges>
			<nk-badge system="salary">
				<nk-badge-item type="system" modelValue="salary"></nk-badge-item>
				<nk-badge-item type="system" modelValue="salary" :title="$t('forms.SalaryItems')"></nk-badge-item>
			</nk-badge>
		</template>
		<div class="d-flex flex-column h-100">
			<div class="nk-card h-100">
				<ag-grid ref="table" :options="tableOption" :data="items" v-model="selectedRows" @preSelectedRowData="(data: SalaryItem) => selectedRow = data" tabindex="105" startup></ag-grid>
			</div>
		</div>
	</nk-page>
</template>

<script lang="ts">
	import $sc from '@/plugins/shortcut.collection'
	import _ from 'lodash'
	import BooleanView from '@/components/form/boolean-label.vue'
	import Tag from '@/components/general/nk-tag.vue'
	import { defineComponent } from 'vue'
	import { post } from '@/plugins/nikan-api'
	import Endpoints from '@nikan/endpoints'
	import { SalaryItemActionTypes, SalaryItemFillTypes, SalaryItemTypes } from '@/enums/salary'
	import { ConvSalaryItem, dataTypes, entryTypes, itemTypes, SalaryItem, operationTypes, SelectedDataEntry, SelectedDataType, SelectedFunctionType, SelectedItemType } from '@/classes/salary/salary-item'
	import modifyItem from '@/views/salary/salary-item/salary-item-modify.vue'
	import { isEmpty } from '@/statics/validators'
	import { Guid, IResponse, IResponseList } from '~/interfaces/base'
	import dialog from '@/plugins/nikan-dialog-plugin'
	import { TYPE } from 'vue-toastification'
	import SalaryItemContractsUsage from '@/components/form/salary/salary-item/salary-item-contracts-usage.vue'
	import SalaryItemDependency from '@/components/form/salary/salary-item/salary-item-dependency.vue'
	import { FirstDataRenderedEvent } from 'ag-grid-community'
	import { RowClickedEvent } from 'ag-grid-community'

	export default defineComponent({
		name: "system-salary-item-list",
		modal: {
			size: 'lg',
			stateUID: 'system-salary-item-list',
			helpTag: 'SystemSalaryItemList'
		},
		props: {
			options: Object,
		},
		data() {
			return {
				isModal: false,
				bottomButtons: {},
				selectedRows: [] as SalaryItem[],
				topButtons: {
					selectButton: {
						title: this.$t('select'),
						shortcut: $sc.find('enter-only'),
						icon: 'check',
						isPrimary: false,
						disabled: false,
						handle: this.handlerBtnSelect,
						// policyClaims: 'Salary_SalaryItem_List',
					},
				},
				itemTypes: itemTypes as SelectedItemType[],
				entryTypes: entryTypes as SelectedDataEntry[],
				operationTypes: operationTypes as SelectedFunctionType[],
				dataTypes: dataTypes as SelectedDataType[],
				items: [] as ConvSalaryItem[],
				selectedRow: [] as any,
				settings: {
					isSelectionMode: false,
					filterByTypes: [] as SalaryItemTypes[],
					filterByfillTypes: [] as SalaryItemFillTypes[],
					filterByActionTypes: [] as SalaryItemActionTypes[],
					excludeByIds: [] as Guid[],
					excludeDisabledItems: true,
					currentItems: [] as SalaryItem[]
				},
				defaultCellClass: 'px-3 nk-border-radius ',
				tableOption: {
					id: this.$options.name,
					pagination: false,

					groupIncludeTotalFooter: false,
					enterToSelect: true,
					cellSelectionAggReport: false,
					sideBar: 'none',
					multiselection: true,
					checkboxesEnabled: true,
					rowSelection: {
						mode: 'multiRow',
						hideDisabledCheckboxes: false,
						checkboxes: (params: any) => {
							return !this.options?.currentItems?.some((x: SalaryItem) => x.systemKey == params.data?.id)
						},
						headerCheckbox: true
					},
					focusAndSelect: {
						allowFocus: true
					},
					onFirstDataRendered: (params: FirstDataRenderedEvent<SalaryItem>) => {
						const nodesToSelect: any[] = []
						params.api.forEachNode((node, index) => {
							if (this.options?.currentItems?.some((x: SalaryItem) => x.systemKey == node.data?.id)) {
								nodesToSelect.push(node)
							}
						})
						params.api.setNodesSelected({ nodes: nodesToSelect, newValue: true })
					},
					onRowClicked: (event: RowClickedEvent<SalaryItem>) => {
						if (this.options?.currentItems?.some((x: SalaryItem) => x.systemKey == event.node.data?.id)) {
							event.node.setSelected(true)
						}
					},
					columnDefs: [
						{
							field: 'rowNumber',
							headerName: this.$t('row-number'),
							width: 80,
							maxWidth: 120,
						},
						{
							field: 'code',
							headerName: this.$t('salary.code'),
							filter: true,
							width: 60
						},
						{
							field: 'name',
							headerName: this.$t('salary.item-name'),
							filter: 'agTextColumnFilter',
						},
						{
							field: 'displayName',
							headerName: this.$t('salary.display-name'),
							filter: 'agTextColumnFilter',
						},
						{
							field: 'includeTax',
							headerName: this.$t('salary.include-tax'),
							contentType: 'enum',
							filter: true,
							cellClass: ' text-center',
							valueFormatter: (data: any) => typeof data.value === 'boolean' && this.$t(`boolean-yes-no.${data.value}`),
							cellRenderer: BooleanView,
							cellRendererParams: (data: any) => {
								return {
									options: {
										useColors: true,
										modelValue: data.value
									},
								}
							},
							width: 75
						},
						{
							field: 'includeInsurance',
							headerName: this.$t('salary.include-insurance'),
							contentType: 'enum',
							filter: true,
							cellClass: ' text-center',
							valueFormatter: (data: any) => typeof data.value === 'boolean' && this.$t(`boolean-yes-no.${data.value}`),
							cellRenderer: BooleanView,
							cellRendererParams: (data: any) => {
								return {
									options: {
										useColors: true,
										modelValue: data.value
									},
								}
							},
							width: 75
						},
						{
							field: 'fillType',
							headerName: this.$t('salary.entry-method'),
							contentType: 'enum',
							filter: true,
							cellClass: 'text-center',
							valueFormatter: (params: any) => {
								return (this.entryTypes as any).find((x: any) => x.id === params.value)?.name
							},
							cellRenderer: Tag,
							cellRendererParams: (params: any) => {
								return {
									options: {
										class: this.defaultCellClass + ' ' + (this.entryTypes as any).find((x: any) => x.id === params.value)?.class,
									},
									value: (this.entryTypes as any).find((x: any) => x.id === params.value)?.name
								}
							},
							width: 120
						},
						{
							field: 'actionType',
							headerName: this.$t('salary.operation-type'),
							contentType: 'enum',
							filter: true,
							cellClass: 'text-center',
							valueFormatter: (params: any) => {
								return (this.operationTypes as any).find((x: any) => x.id === params.value)?.name
							},
							cellRenderer: Tag,
							cellRendererParams: (params: any) => {
								return {
									options: {
										class: this.defaultCellClass + ' ' + (this.operationTypes as any).find((x: any) => x.id === params.value)?.class,
									},
									value: (this.operationTypes as any).find((x: any) => x.id === params.value)?.name
								}
							},
							width: 120
						},
						{
							field: 'type',
							headerName: this.$t('salary.choice-type'),
							contentType: 'enum',
							filter: true,
							cellClass: 'text-center',
							valueFormatter: (params: any) => {
								return (this.itemTypes as any).find((x: any) => x.id === params.value)?.name
							},
							cellRenderer: Tag,
							cellRendererParams: (params: any) => {
								return {
									options: {
										class: this.defaultCellClass + ' ' + (this.itemTypes as any).find((x: any) => x.id === params.value)?.class,
									},
									value: (this.itemTypes as any).find((x: any) => x.id === params.value)?.name
								}
							},
							width: 120
						},
						{
							field: 'isActive',
							headerName: this.$t('salary.active'),
							filter: true,
							contentType: 'enum',
							cellClass: ' text-center',
							valueFormatter: (data: any) => typeof data.value === 'boolean' && this.$t(`boolean-yes-no.${data.value}`),
							cellRenderer: BooleanView,
							cellRendererParams: (data: any) => {
								return {
									options: {
										useColors: true,
										modelValue: data.value
									},
								}
							},
							width: 75
						}
					]
				} as any,
			}
		},
		created() {
			this.settings = _.merge({}, this.settings, this.options)
		},
		mounted() {
			this.isModal = this.grandParentType() === 'modal'
			this.getData()
			this.prepareWindow(this.$t(`nav.trans.salary-items`), [this.topButtons.selectButton])
		},
		methods: {
			handlerBtnSelect() {
				if (!this.selectedRows.length)
					return this.$toast(this.$t('document.m.select-row'))

				for (let item of this.selectedRows.filter(x => x.usedSalaryItemIds)) {
					let notUsedItems = item.usedSalaryItemIds.split(',').filter(ui => !this.selectedRows.some(s => s.id === ui))
					if (notUsedItems.length) {
						let text = notUsedItems.map(x => this.items.find(item => item.id === x)?.name).join(', ')
						dialog({
							title: 'خطا',
							body: this.$t('salary.salary-item.m.used-item-required', { name: item.name, used: text }),
							type: 'error'
						})?.then(res => { })
							.catch(error => { })
						return
					}
				}

				var wait = this.$wait(this.$el)
				post(Endpoints.getUrl('salaryItem.copySystemSalaryItems'), { systemSalaryItems: this.selectedRows })
					.then((result: IResponseList<ConvSalaryItem>) => {
						this.resolve(this.selectedRows)
					})
					.finally((f: any) => {
						wait.end()
					})
			},
			getData(requestOptions: any = null, selectRowCode: string | null = null) {
				var wait = this.$wait(this.$el)
				post(Endpoints.getUrl('salaryItem.systemSalaryItemList'), {}, (result: IResponseList<ConvSalaryItem>) => {
					this.items = result.data.items
					wait.end()
				}, requestOptions)
			},
		}
	})
</script>

<style scoped></style>